// React hook for live data
import { useState, useEffect, useRef, useCallback } from 'react';
// ──────────────────────────────────────────────────────────
// Live data fetching for PP MAP v5
// 
// Fetches free, public market data and refreshes every 15 minutes.
// Designed to fail silently — if a source is down, the manual
// value remains and the user sees a "stale" indicator.
//
// Sandboxed-iframe-safe: uses fetchT with manual AbortController
// (AbortSignal.timeout breaks in some sandboxed contexts).
// ──────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

// Detect Claude.ai sandboxed iframe — skip fetches there
const IS_SANDBOXED = (() => {
  try {
    const href = window.location.href;
    if (href === 'about:srcdoc' || href === 'about:blank' || href === '') return true;
    if (window.self !== window.top) {
      try { void window.top.location.href; } catch (e) { return true; }
    }
    return false;
  } catch (e) { return true; }
})();

function fetchT(url, opts = {}, ms = 8000) {
  if (IS_SANDBOXED) return Promise.reject(new Error('sandboxed'));
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// ── Sources ────────────────────────────────────────────────

// All CORS-blocked feeds (Yahoo Finance, Polymarket, Sina DCE) are fetched
// server-side via /api/live-prices so CN users without VPN get live data too.
async function fetchViaProxy() {
  try {
    const r = await fetchT('/api/live-prices', {}, 10000);
    const j = await r.json();
    const out = {};
    if (typeof j?.data?.wti_price        === 'number') out.wti_price        = j.data.wti_price;
    if (typeof j?.data?.brent_price      === 'number') out.brent_price      = j.data.brent_price;
    if (typeof j?.data?.polymarket_peace === 'number') out.polymarket_peace = j.data.polymarket_peace;
    if (typeof j?.data?.dce_futures_m1   === 'number') out.dce_futures_m1   = j.data.dce_futures_m1;
    return out;
  } catch (e) {}
  return {};
}

async function fetchCNY() {
  try {
    const r = await fetchT('https://open.er-api.com/v6/latest/USD', {}, 6000);
    const j = await r.json();
    const cny = j?.rates?.CNY;
    if (typeof cny === 'number') return { currency_rate: +cny.toFixed(4) };
  } catch (e) {}
  return {};
}

// ── Public API ────────────────────────────────────────────

export async function fetchAllLive() {
  if (IS_SANDBOXED) {
    return {
      values: {},
      timestamps: {},
      errors: { _sandboxed: true },
    };
  }

  const now = Date.now();
  const [proxy, cny] = await Promise.allSettled([
    fetchViaProxy(),
    fetchCNY(),
  ]);

  const values = {};
  const timestamps = {};
  const errors = {};

  function ingest(result, srcKey) {
    if (result.status === 'fulfilled' && result.value) {
      Object.entries(result.value).forEach(([k, v]) => {
        values[k] = v;
        timestamps[k] = now;
      });
    } else {
      errors[srcKey] = result.reason?.message || 'fetch_failed';
    }
  }

  ingest(proxy, 'proxy');
  ingest(cny, 'cny');

  return { values, timestamps, errors };
}



export function useLiveData(onValuesUpdate) {
  const [state, setState] = useState({
    values: {},
    timestamps: {},
    errors: {},
    isLoading: false,
    lastFetchAt: null,
  });
  const onValuesUpdateRef = useRef(onValuesUpdate);
  onValuesUpdateRef.current = onValuesUpdate;

  const refresh = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true }));
    const result = await fetchAllLive();
    setState({
      ...result,
      isLoading: false,
      lastFetchAt: Date.now(),
    });
    if (onValuesUpdateRef.current && Object.keys(result.values).length) {
      onValuesUpdateRef.current(result.values);
    }
  }, []);

  useEffect(() => {
    if (IS_SANDBOXED) return;
    // Initial fetch on mount
    refresh();
    // Refresh every 15 minutes
    const id = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    isSandboxed: IS_SANDBOXED,
    refresh,
  };
}

// Helper: get a human "X min ago" or "Just now" label
export function formatAge(ts, lang = 'en') {
  if (!ts) return lang === 'zh' ? '从未' : 'never';
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 30)   return lang === 'zh' ? '刚刚' : 'just now';
  if (sec < 60)   return lang === 'zh' ? `${sec}s` : `${sec}s ago`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    return lang === 'zh' ? `${m}m前` : `${m}m ago`;
  }
  const h = Math.floor(sec / 3600);
  return lang === 'zh' ? `${h}h前` : `${h}h ago`;
}

// Helper: classify staleness
export function getFreshness(ts) {
  if (!ts) return 'missing';
  const minAgo = (Date.now() - ts) / 60000;
  if (minAgo < 20) return 'live';      // <20 min = live
  if (minAgo < 60) return 'stale';     // 20-60 min = stale
  return 'expired';                    // >60 min = expired
}
